import { useState, useEffect } from 'react';
import {
  Flex,
  View,
  Text,
  Heading,
  Button,
  Card,
  Badge,
  Loader,
  SelectField
} from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import JobDetailsModal from './JobDetailsModal';

const ListingsDisplay = ({ 
  variant = 'full',
  sortedInternships = [],
  startIndex = 0,
  endIndex = 10,
  sortBy = 'relevance',
  setSortBy,
  itemsPerPage = 10,
  currentPage = 1,
  setCurrentPage,
  handleInternshipClick,
  toggleSaveListing,
  isSaved,
  compatMap = {},
  maxItems,
  showPagination = true,
  showSorting = true,
  showFilters = true,
  preloadedJobs = [],
  loadingJobs = false,
  cvFileName = null 
}) => {
  const [internships, setInternships] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error] = useState(null);
  
  // Modal state for compact variant
  const [showModal, setShowModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  // Handler functions for modal
  const handleJobClick = (job) => {
    console.log('Job clicked:', job);
    
    const jobWithCompatibility = {
      ...job,
      score: job.score || job.matchScore || 0,
      scoreDetails: job.scoreDetails || {
        explanation: job.scoreDetails?.explanation || job.scoreHighlights?.recommendation || '',
        keyMatches: job.scoreDetails?.keyMatches || job.scoreDetails?.key_matches || [],
        missingSkills: job.scoreDetails?.missingSkills || job.scoreDetails?.missing_skills || [],
        recommendations: job.scoreDetails?.recommendations || []
      }
    };
    
    console.log('Job being sent to modal:', jobWithCompatibility);
    setSelectedJob(jobWithCompatibility);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedJob(null);
  };

  const handleApply = (job) => {
    const applyUrl = job.apply_url || job.url || job.applyUrl;
    if (applyUrl) {
      window.open(applyUrl, '_blank', 'noopener,noreferrer');
    }
  };

  useEffect(() => {
    if (variant === 'compact') {
      // ONLY use preloaded jobs - never fetch own data
      if (preloadedJobs && preloadedJobs.length > 0) {
        console.log('Using preloaded jobs from HomePage:', preloadedJobs.length);
        console.log('LISTINGSDISPLAY: Raw preloadedJobs received:', preloadedJobs);
        console.log('LISTINGSDISPLAY: First job raw data:', preloadedJobs[0]);
        
        // Transform the preloaded jobs to match our component's expected format
        const transformedJobs = preloadedJobs.slice(0, maxItems || 5).map(job => ({
          id: job.jobId || job.id,
          title: job.jobTitle  || 'Untitled',
          company: job.company || '',
          location: job.location || '',
          locationType: job.locationType || 'On-site',
          salary: job.salary || 'Not specified',
          description: job.description || 'No description available',
          requirements: job.requirements || [],
          apply_url: job.url || job.apply_url || '',
          url: job.url || job.apply_url || '',
          source: job.source || 'Saved',
          industry: job.industry || job.category || 'General',
          jobType: job.jobType || 'Full-time',
          postedDays: 0,
          score: job.score || job.matchScore || 0,
          matchScore: job.score || job.matchScore || 0,
          savedAt: job.savedAt,
          scoreDetails: job.scoreDetails || null,
          scoreHighlights: job.scoreHighlights || null
        }));
        
        setInternships(transformedJobs);
        setLoading(false);
      } else {
        // No preloaded data - just show empty state
        console.log('No preloaded jobs provided for compact variant');
        setInternships([]);
        setLoading(false);
      }
    }
  }, [variant, maxItems, preloadedJobs]);

  const getCompatibilityColor = (score) => {
    if (score >= 80) return '#2ECC71';
    if (score >= 60) return '#F39C12';
    if (score >= 40) return '#E67E22';
    return '#E74C3C';
  };

  // Compact variant for HomePage
  if (variant === 'compact') {
    // Function to get medal emoji based on rank
    const getMedal = (index) => {
      switch(index) {
        case 0: return '🥇';
        case 1: return '🥈';
        case 2: 
        case 3:
        case 4: return '🥉';
        default: return null;
      }
    };

    return (
      <>
        <Card 
          borderRadius="12px" 
          padding="1.5rem"
          style={{ 
            background: 'rgba(30, 35, 45, 0.85)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.1)',
            boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
            height: '100%'
          }}
        >
          <Flex direction="column" gap="1rem" height="100%">
            <Heading level={3} style={{ color: 'white', fontSize: '1.3rem' }}>
              Most Compatible Jobs
            </Heading>
            {cvFileName && (
              <Text 
                fontSize="0.85rem" 
                color="rgba(255,255,255,0.6)"
                style={{ marginLeft: '0.25rem' }}
              >
                based on: {cvFileName}
              </Text>
            )}
            {loading || loadingJobs ? (
              <Flex justifyContent="center" alignItems="center" flex="1">
                <Loader size="large" />
              </Flex>
            ) : error ? (
              <Text color="rgba(255,255,255,0.7)" textAlign="center">{error}</Text>
            ) : internships.length === 0 ? (
              <Flex direction="column" gap="1rem" justifyContent="center" alignItems="center" flex="1">
                <Text fontSize="2rem">🎯</Text>
                <Text color="rgba(255,255,255,0.7)" textAlign="center">
                  Upload your CV to see job recommendations based on compatibility
                </Text>
              </Flex>
            ) : (
              <Flex direction="column" gap="0.75rem">
                {internships.map((job, index) => {
                  const medal = getMedal(index);
                  return (
                    <Card
                      key={job.id}
                      padding="1rem"
                      borderRadius="8px"
                      backgroundColor="rgba(255,255,255,0.05)"
                      border="1px solid rgba(102, 126, 234, 0.2)"
                      style={{ 
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        position: 'relative'
                      }}
                      onClick={() => handleJobClick(job)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)';
                        e.currentTarget.style.transform = 'translateX(4px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)';
                        e.currentTarget.style.transform = 'translateX(0)';
                      }}
                    >
                      {medal && (
                        <View
                          style={{
                            position: 'absolute',
                            top: '-8px',
                            left: '-8px',
                            width: '28px',
                            height: '28px',
                            borderRadius: '50%',
                            background: 'rgba(30, 35, 45, 0.95)',
                            border: '2px solid rgba(255,255,255,0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1.2rem',
                            zIndex: 1
                          }}
                        >
                          {medal}
                        </View>
                      )}
                      <Flex justifyContent="space-between" alignItems="center">
                        <Flex direction="column" gap="0.25rem" flex="1">
                          <Text 
                            fontWeight="600" 
                            color="white" 
                            fontSize="0.95rem"
                            style={{ 
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {job.title}
                          </Text>
                          <Text color="rgba(255,255,255,0.7)" fontSize="0.85rem">
                            {job.company} • {job.location}
                          </Text>
                        </Flex>
                        
                        <View
                          style={{
                            width: '50px',
                            height: '50px',
                            borderRadius: '50%',
                            background: `conic-gradient(${getCompatibilityColor(job.score)} ${job.score * 3.6}deg, rgba(255,255,255,0.1) 0deg)`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            marginLeft: '1rem'
                          }}
                        >
                          <View
                            style={{
                              width: '42px',
                              height: '42px',
                              borderRadius: '50%',
                              background: 'rgba(30, 35, 45, 0.9)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: '1px solid rgba(255,255,255,0.1)'
                            }}
                          >
                            <Text 
                              color="white" 
                              fontWeight="bold" 
                              fontSize="0.9rem"
                            >
                              {job.score}
                            </Text>
                          </View>
                        </View>
                      </Flex>
                    </Card>
                  );
                })}
                
                {/* Placeholder text when there are fewer than 5 jobs */}
                {internships.length > 0 && internships.length < 5 && (
                  <Card
                    padding="1rem"
                    borderRadius="8px"
                    backgroundColor="rgba(255,255,255,0.03)"
                    border="1px solid rgba(102, 126, 234, 0.1)"
                    style={{ 
                      marginTop: '0.75rem',
                      textAlign: 'center'
                    }}
                  >
                    <Text 
                      fontSize="0.85rem" 
                      color="rgba(255,255,255,0.5)"
                      style={{ fontStyle: 'italic', lineHeight: '1.4' }}
                    >
                      Your highest-scoring job matches appear here (up to 5). Click any job to view detailed compatibility analysis and application advice tailored to your CV.
                    </Text>
                  </Card>
                )}
              </Flex>
            )}
          </Flex>
        </Card>

        {/* Modal for compact variant */}
        {showModal && selectedJob && (
          <JobDetailsModal
            showModal={showModal}
            job={selectedJob}
            onClose={handleCloseModal}
            onApply={handleApply}
            readOnly={true}
            compatibilityResult={selectedJob.compatibilityResult || (
              selectedJob.score > 0 && selectedJob.scoreDetails ? {
                score: selectedJob.score,
                explanation: selectedJob.scoreDetails.explanation || '',
                key_matches: selectedJob.scoreDetails.keyMatches || [],
                missing_skills: selectedJob.scoreDetails.missingSkills || [],
                recommendations: selectedJob.scoreDetails.recommendations || []
              } : null
            )}
          />
        )}
      </>
    );
  }

  // Full variant for Listings page
  return (
    <View>
      {showSorting && (
        <Card 
          borderRadius="12px" 
          padding="1.5rem" 
          marginBottom="2rem"
          style={{
            background: 'rgba(30, 35, 45, 0.85)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.1)',
            boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
          }}
        >
          <Flex justifyContent="space-between" alignItems="center">
            <Heading level={3} style={{ color: 'white' }}>
              {sortedInternships.length} Internships Found
            </Heading>
            
            <SelectField
              label="Sort by"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{ 
                minWidth: '200px',
                backgroundColor: 'rgba(255,255,255,0.1)',
                color: 'white',
                borderRadius: '8px'
              }}
            >
              <option value="relevance">Relevance</option>
              <option value="recent">Most Recent</option>
              <option value="salary">Highest Salary</option>
            </SelectField>
          </Flex>
        </Card>
      )}

      <Flex direction="column" gap="1rem">
        {sortedInternships.slice(startIndex, endIndex).map((internship) => {
          const compatScore = compatMap[internship.id]?.score;
          const saved = isSaved && isSaved(internship.id);
          
          return (
            <Card 
              key={internship.id}
              borderRadius="12px" 
              padding="1.5rem"
              style={{
                background: 'rgba(30, 35, 45, 0.85)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.1)',
                boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                cursor: 'pointer',
                transition: 'all 0.3s'
              }}
              onClick={() => handleInternshipClick(internship)}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 15px 40px rgba(0,0,0,0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.3)';
              }}
            >
              <Flex alignItems="center" gap="1.5rem">
                <View
                  backgroundColor="rgba(102, 126, 234, 0.1)"
                  width="80px"
                  height="80px"
                  borderRadius="8px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink="0"
                >
                  <Text color="rgba(102, 126, 234, 0.8)" fontSize="0.875rem" fontWeight="bold">
                    {internship.company.substring(0, 3).toUpperCase()}
                  </Text>
                </View>

                <Flex direction="column" gap="0.5rem" flex="1">
                  <Heading level={4} fontSize="1.25rem" style={{ color: 'white' }}>
                    {internship.title}
                  </Heading>
                  <Text fontSize="1rem" color="rgba(255,255,255,0.9)">
                    {internship.company}
                  </Text>
                  <Flex gap="0.5rem" alignItems="center" flexWrap="wrap">
                    <Text color="rgba(255,255,255,0.7)" fontSize="0.9rem">
                      {internship.location} ({internship.locationType})
                    </Text>
                    <Text color="rgba(255,255,255,0.5)">•</Text>
                    <Text color="rgba(255,255,255,0.7)" fontSize="0.9rem">
                      {internship.salary}
                    </Text>
                    <Text color="rgba(255,255,255,0.5)">•</Text>
                    <Text color="rgba(255,255,255,0.7)" fontSize="0.9rem">
                      {(() => {
                        // Extract source from id field
                        if (internship.id) {
                          const match = internship.id.match(/^([a-zA-Z]+)[_-]/);
                          if (match) {
                            const source = match[1];
                            return source.charAt(0).toUpperCase() + source.slice(1).toLowerCase();
                          }
                        }
                        return 'Direct';
                      })()}
                    </Text>

                    {saved && (
                      <>
                        <Text color="rgba(255,255,255,0.5)">•</Text>
                        <Badge 
                          style={{
                            backgroundColor: 'rgba(46, 204, 113, 0.2)',
                            color: '#2ECC71',
                            border: '1px solid rgba(46, 204, 113, 0.3)',
                            fontSize: '0.7rem'
                          }}
                        >
                          SAVED
                        </Badge>
                      </>
                    )}
                  </Flex>
                </Flex>

                {typeof compatScore === 'number' && (
                  <View
                    style={{
                      width: '64px',
                      height: '64px',
                      borderRadius: '50%',
                      background: `conic-gradient(${getCompatibilityColor(compatScore)} ${compatScore * 3.6}deg, rgba(255,255,255,0.12) 0deg)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <View
                      style={{
                        width: '52px',
                        height: '52px',
                        borderRadius: '50%',
                        background: 'rgba(20,22,28,0.9)',
                        border: '1px solid rgba(255,255,255,0.15)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: '800',
                        fontSize: '1.1rem'
                      }}
                    >
                      {compatScore}
                    </View>
                  </View>
                )}

                <Button
                  variation="primary"
                  size="large"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSaveListing(internship);
                  }}
                  style={{
                    background: saved
                      ? 'rgba(46, 204, 113, 0.2)'
                      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    border: saved ? '2px solid #2ECC71' : 'none',
                    minWidth: '120px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: saved ? '#2ECC71' : 'white',
                    borderRadius: '8px'
                  }}
                >
                  {saved ? 'Saved' : 'Save'}
                </Button>
              </Flex>
            </Card>
          );
        })}
      </Flex>

      {showPagination && sortedInternships.length > itemsPerPage && (
        <Flex justifyContent="center" gap="0.5rem" marginTop="2rem">
          {Array.from({ length: Math.ceil(sortedInternships.length / itemsPerPage) }, (_, i) => (
            <Button
              key={i + 1}
              size="small"
              variation={currentPage === i + 1 ? 'primary' : 'link'}
              onClick={() => setCurrentPage(i + 1)}
              style={{
                minWidth: '40px',
                background: currentPage === i + 1
                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                  : 'transparent',
                color: 'white',
                border: currentPage === i + 1 ? 'none' : '1px solid rgba(255,255,255,0.3)'
              }}
            >
              {i + 1}
            </Button>
          ))}
        </Flex>
      )}
    </View>
  );
};

export default ListingsDisplay;